import { Article, SearchMode } from "@/features/search/types/search";

// Simple environment-based API URL helper
const getApiUrl = () => {
  const isDev = import.meta.env.DEV;
  if (isDev) {
    // Use environment variable if set, otherwise default to localhost
    return import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";
  }
  return "https://supabase-worker.marsescobin.workers.dev";
};

export async function getEmbedding(query: string) {
  try {
    console.log("Getting embedding for query:", query);
    const response = await fetch(
      "https://openai-worker.marsescobin.workers.dev/api/embedding",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      }
    );

    if (!response.ok) {
      console.error("Error getting embedding:", response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const embedding = await response.json();
    return embedding;
  } catch (error) {
    console.error("Error generating embedding:", error);
    return null;
  }
}

export async function fetchArticlesByEmbedding({
  embedding,
  daysAgo,
  searchMode = "summary",
  matchCount,
  matchThreshold = 70,
}: {
  embedding: number[];
  daysAgo: number;
  searchMode?: SearchMode;
  matchCount?: number;
  matchThreshold?: number;
}) {
  try {
    const payload = {
      embedding,
      daysAgo,
      searchMode,
      matchCount,
      matchThreshold,
    };
    console.log("Sending request with payload:", payload);

    const response = await fetch(`${getApiUrl()}/api/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Search API error response:", errorText);
      throw new Error(
        `HTTP error in fetchArticlesByEmbedding! status: ${response.status}, message: ${errorText}`
      );
    }
    const articles = await response.json();
    console.log("Raw API response:", articles);
    return articles;
  } catch (error) {
    console.error("Error fetching articles by embedding:", error);
    return [];
  }
}

export async function fetchArticlesByTag(tag: string, timeRange: number) {
  const response = await fetch(
    "https://supabase-worker.marsescobin.workers.dev/api/articles-by-tag",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tag, daysAgo: timeRange }),
    }
  );

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
}

export async function generateCliffNote(
  searchQuery: string,
  articles: Article[]
) {
  try {
    if (!articles?.length) {
      console.log("No articles provided for cliff note generation");
      return null;
    }

    const articlesContext = articles
      .map(
        (article) =>
          `Title: ${article.title},
         Summary: ${article.summary},
         Publication: ${article.author},
         Publish Date: ${article.publish_date},
         URL: ${article.link}`
      )
      .join("\n");

    const response = await fetch(
      "https://openai-worker.marsescobin.workers.dev/api/cliff-note",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ articlesContext, searchQuery }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      openAICliffNote: data.openAICliffNote,
      searchQuery,
      articles,
    };
  } catch (error) {
    console.error("Error generating cliff note:", error);
    return null;
  }
}
